import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Mic, Type, Calendar, PhoneCall, ArrowRight, X, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUDGE_CONFIG = {
  voice: { icon: Mic, color: 'text-amber-400 bg-amber-500/10', label: 'Choose your AI voice', link: '/settings/assistant' },
  greeting: { icon: Type, color: 'text-violet-400 bg-violet-500/10', label: 'Set up your greeting', link: '/settings/assistant' },
  calendar: { icon: Calendar, color: 'text-teal-400 bg-teal-500/10', label: 'Set your availability', link: '/settings' },
  test_call: { icon: PhoneCall, color: 'text-emerald-400 bg-emerald-500/10', label: 'Test your AI assistant', link: '/simulate' },
};

export function SetupNudges() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    const fetchNudges = async () => {
      try {
        const { data } = await (await import('axios')).default.get(`${API}/onboarding/nudges`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNudges(data.nudges || []);
      } catch { /* silent */ }
    };
    fetchNudges();
  }, [token]);

  const visible = nudges.filter(n => !dismissed.includes(n.type));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="setup-nudges">
      {visible.map(n => {
        const config = NUDGE_CONFIG[n.type];
        if (!config) return null;
        const Icon = config.icon;
        return (
          <div key={n.type} className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg"
            data-testid={`nudge-${n.type}`}>
            <div className={`h-8 w-8 rounded-lg ${config.color} flex items-center justify-center shrink-0`}>
              <Icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-foreground font-medium">{config.label}</p>
              <p className="text-[10px] text-muted-foreground">Complete this to get the most from your AI</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => navigate(config.link)}
              className="text-emerald-400 hover:text-primary text-xs gap-1 h-7 px-2 shrink-0"
              data-testid={`nudge-action-${n.type}`}>
              Set up <ArrowRight size={10} />
            </Button>
            <button onClick={() => setDismissed(p => [...p, n.type])} className="text-muted-foreground hover:text-muted-foreground shrink-0">
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function EmptyLeadsState() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-8 px-4" data-testid="empty-leads-state">
      <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
        <PhoneCall size={20} className="text-emerald-400" />
      </div>
      <h3 className="text-base font-semibold text-foreground">No leads yet</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
        Your AI is ready to take calls. Share your phone number to start receiving leads.
      </p>
      <Button size="sm" onClick={() => navigate('/leads')}
        className="mt-3 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 text-xs gap-1"
        data-testid="add-first-lead-btn">
        Add a lead manually <ArrowRight size={10} />
      </Button>
    </div>
  );
}
